// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '../../lib/base';
import { KeyboardArrowDownIcon } from '../../lib/icons';
import VideoGrid from './VideoGrid';

interface Section {
  readonly id: string;
  readonly title: string;
  readonly content: readonly AccordionType[];
}

function createSection({ id, title, content }: Section) {
  return (
    <Box sx={{ py: 1 }} key={id}>
      <Typography sx={{ py: 1 }} variant="h4">
        {title}
      </Typography>
      {content.map(createAccordionItem)}
    </Box>
  );
}

interface AccordionType {
  readonly id: string;
  readonly aria: string;
  readonly summary: string;
  readonly details: React.ReactNode;
}

function createAccordionItem({ id, aria, summary, details }: AccordionType) {
  return (
    <Accordion key={id}>
      <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} id={id} aria-controls={aria}>
        <Typography variant="h5">{summary}</Typography>
      </AccordionSummary>
      <AccordionDetails>{details}</AccordionDetails>
    </Accordion>
  );
}

// MM
const mm: Section = {
  id: 'microgreen-manager',
  title: 'Microgreen Manager',
  content: [
    {
      id: 'what-is-microgreenmanager',
      aria: 'what-is-microgreenmanager-content',
      summary: 'What is Microgreen Manager?',
      details: (
        <Typography>Microgreen Manager is a web application that helps you manage your microgreen business.</Typography>
      ),
    },
    {
      id: 'how-do-i-get-started',
      aria: 'how-do-i-get-started-content',
      summary: 'How do I get started?',
      details: (
        <>
          <Typography>
            To get started, go to the Crops page and start entering your Crops. Once that's done, go to the Blends page
            and start creating your Blends. Now you have a library of Crops and Blends. You can now go to the Orders
            page and start creating Orders. You can create one-time Orders or Recurring Orders.
          </Typography>
          <br />
          <Typography>
            Once you create an Order, Microgreen Manager will automatically create Plantings and Tasks associated with
            that Order that you can use to track your progress.
          </Typography>
        </>
      ),
    },
  ],
};
const priceCalculator: Section = {
  id: 'price-calculator',
  title: 'Price Calculator',
  content: [
    {
      id: 'how-do-i-use-the-price-calculator',
      aria: 'how-do-i-use-the-price-calculator-content',
      summary: 'How do I use the Price Calculator?',
      details: (
        <ul>
          {[
            'Fill out the parameters and our tool will automatically calculate your costs, revenue, and margin per tray and per container.',
            'Use the first section to calculate your cost per try and how much you should charge for your product.',
            'Use the second section to calculate how much you need to grow and sell to make a profit for your farm.',
            "Almost all fields are optional. So if there's a field you don't want to use, just leave it blank.",
            'Note that the calculator is using as many decimal places as possible to do all calculations, but displays only two decimal places for the results.',
            'The results will update automatically as you type.',
          ].map((text, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <li key={idx}>
              <Typography variant="body1">{text}</Typography>
            </li>
          ))}
        </ul>
      ),
    },
  ],
};
// Crops
const crops: Section = {
  id: 'crops',
  title: 'Crops',
  content: [
    {
      summary: 'What is a Crop?',
      id: 'what-is-a-crop',
      aria: 'what-is-a-crop-content',
      details: (
        <>
          <Typography>
            A Crop is a single type of microgreen. A crop can be generic, such as "Basil", or specific, such as "Purple
            Basil", "Genovese Basil", or "Lemon Basil".
          </Typography>
        </>
      ),
    },
    {
      id: 'what-happens-when-i-create-default-crops',
      aria: 'what-happens-when-i-create-default-crops-content',
      summary: 'Creating Default Crops',
      details: (
        <>
          <Typography>
            When you press "Add Default Crops" on the Crops page, Microgreen Manager will create a default set of Crops
            for you. It will create about 32 default Crops, including some common Crops like Arugula, Basil, Broccoli,
            Cabbage, Kale, Kohlrabi, Radish, and Sunflower. This is just a starting point, and you can edit or delete
            any of these Crops, or add new Crops, as you see fit. It will also create a single default Blend called
            "Brassica Blend".
          </Typography>
          <br />
          <Typography>
            Note that if you already have Crops in your database, pressing "Add Default Crops" will not overwrite your
            existing Crops. It will also not create any Crops or Blends from the list of defaults if a Crop or Blend
            with the same name already exists in your database. So if you already have a Crop called "Arugula" in your
            database, pressing "Add Default Crops" will not create a new Crop called "Arugula". It will only create new
            Crops and Blends.
          </Typography>
        </>
      ),
    },
    {
      id: 'what-happens-when-i-edit-a-crop',
      aria: 'what-happens-when-i-edit-a-crop-content',
      summary: 'Edit a Crop',
      details: (
        <Typography>
          When you edit a Crop, depending on which fields you edit, Microgreen Manager will automatically update all the
          Plantings and Tasks associated with that Crop that haven't been started yet.
        </Typography>
      ),
    },
    {
      id: 'what-does-crop-buffer-percentage-do',
      aria: 'what-does-crop-buffer-percentage-do-content',
      summary: 'Crop Buffer Percentage',
      details: (
        <Typography>
          Crop buffer percentage is a way to automatically add a buffer to the number of trays you need to plant for an
          Order. So when you enter the amount needed for your order, Microgreen Manager will automatically calculate how
          many trays will be needed to fulfill that part of the Order. Then, if the Crop has a buffer percentage
          defined, it will automatically increase the number of trays needed by that percentage. For example, if you
          enter 100 trays for an Order, and the Crop has a buffer percentage of 10%, then Microgreen Manager will
          automatically increase the number of trays needed to 110 trays. This is useful if you want to automatically
          add a buffer to your Orders for an exra margin of safety.
        </Typography>
      ),
    },
  ],
};
// Blends
const blends: Section = {
  id: 'blends',
  title: 'Blends',
  content: [
    {
      summary: 'What is a Blend?',
      id: 'what-is-a-blend',
      aria: 'what-is-a-blend-content',
      details: (
        <>
          <Typography>
            A combination of two or more crops. Each crop in a blend contributes to the weight of the blend based on a
            percentage. For example, the “Nutrition Mix” is a combination of three crops - broccoli, cabbage, and
            kohlrabi. Each crop could contribute 33.3%. Each crop's contribution could also be 1%, 1%, and 98%,
            respectively.
          </Typography>
        </>
      ),
    },
  ],
};
// Orders
const orders: Section = {
  id: 'orders',
  title: 'Orders',
  content: [
    {
      summary: 'What is an Order?',
      id: 'what-is-an-order',
      aria: 'what-is-an-order-content',
      details: (
        <>
          <Typography>
            The combination of a customer name, delivery date, order frequency, and crops or blends. Submitting an order
            will trigger the software to create Plantings, Tasks, and Harvests.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Edit an Order',
      id: 'what-happens-when-i-edit-an-order',
      aria: 'what-happens-when-i-edit-an-order-content',
      details: (
        <>
          <Typography>
            If you just edit the Order name or Order notes, then Microgreen Manager will update the Order's name and
            notes, and nothing else will change.
          </Typography>
          <br />
          <Typography>
            If you edit the Order's delivery date, then Microgreen Manager will delete any Planting that hasn't been
            started yet (where status is "planned"), and all of the associated Planting's Tasks. Any Plantings that are
            in progress or completed will not be deleted, but will be removed from the Order, and will be marked as
            surplus. The Tasks associated with those Plantings will also be removed from the Order, but will not be
            deleted. Then Microgreen Manager will create new Plantings and Tasks for the Order for the new delivery
            time. If you also changed the crops or blends for the Order, then Microgreen Manager will use those new
            inputs to create the new Plantings and Tasks. This means that if you change the delivery date for an Order,
            you are essentially creating an entirely new Order, except that anything you've already planted for that
            Order will be marked as surplus and will show up in your dashboard as surplus, which you can then do with
            what you want.
          </Typography>
          <br />
          <Typography>
            If you change just the crops or blends for the Order, then things get more interesting. Microgreen Manager
            will go through all the new inputs for the Order and recalculate exactly how many trays will need to be
            planted for each crop to satisfy the Order with the new inputs. Then it will compare those tray counts to
            the previous tray counts for the Order. For each Crop it will calculate the difference between the new tray
            count and the old tray count.
          </Typography>
          <br />
          <Typography>
            If the difference is negative (meaning less trays are needed), then Microgreen Manager will check if that
            Crop has been planted yet (if the Planting is marked as "in progress"). If so, then Microgreen Manager will
            modify the existing Planting by decreasing the tray count for that Crop by the difference, and also by
            creating a new Planting with the difference and marking it as surplus. If the existing Planting hasn't been
            started yet, then it will simply modify the tray count to the new value.
          </Typography>
          <br />
          <Typography>
            If the difference is positive (meaning more trays are needed), then Microgreen Manager will check if that
            Crop has been planted yet. If it has not been planted yet, then it will simply modify the tray count to the
            new value. if it has been planted already, then it is presumably too late to plant more trays for that Crop.
            So Microgreen Manager will look to see if there are existing surpluses in the farm for that Crop to fulfill
            the increase in the Order. If it finds that there does exist surplus for that Crop currently available in
            the farm, then it will assign as much available surplus as it can to the existing Planting (and thus to the
            Order). If after all that there still wasn't enough to fulfill the increase in the Order, then you will see
            on your dashboard that you have a deficit for that Crop for that Order. From there you can decide how to
            handle that deficit.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Cancel an Order',
      id: 'what-happens-when-i-cancel-an-order',
      aria: 'what-happens-when-i-cancel-an-order-content',
      details: (
        <>
          <Typography>
            When you cancel an indivial Order, Microgreen Manager will not delete the Order information from the
            database, but will mark it as "cancelled". This is so you have historical records of your Orders. Cancelling
            an Order will also affect the Plantings and Tasks for that Order. If the Order hasn't been started (you
            haven't marked any Plantings or Tasks as "in progress" or "completed"), then Microgreen Manager will
            permanently delete all the Plantings and Tasks associated with that Order. If the Order has been started,
            then Microgreen Manager will delete any Plantings that haven't been started yet (where status is "in
            progress"), and all the Tasks associated with those Plantings. Any Plantings or Tasks that have a status of
            "in progress" or "completed" or "cancelled" will not be deleted. Any Plantings that have been started will
            be marked as surplus and will then show up in your dashboard as surplus so you can track your surplus and
            plan accordingly.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Mark an Order as Complete',
      id: 'what-happens-when-i-mark-an-order-as-complete',
      aria: 'what-happens-when-i-mark-an-order-as-complete-content',
      details: (
        <>
          <Typography>
            When you mark an Order as complete, the software will automatically mark all the Plantings and Tasks
            associated with that Order as complete (if they aren't already marked as complete). So if you've completed
            all the Tasks for an Order, you don't have to mark all the Tasks as complete, just mark the Order as
            complete and the software will automatically mark all the Tasks and Plantings for that Order as complete.
          </Typography>
        </>
      ),
    },
  ],
};
// Recurring Orders
const recurringOrders: Section = {
  id: 'recurring-orders',
  title: 'Recurring Orders',
  content: [
    {
      summary: 'Create a Recurring Order',
      id: 'what-happens-when-i-create-a-recurring-order',
      aria: 'what-happens-when-i-create-a-recurring-order-content',
      details: (
        <>
          <Typography>
            When you create a Recurring Order, Microgreen Manager will add that Recurring Order to the database, and
            will automatically see if any new Orders need to be started within the next 7 days. If so, then it will
            create those Orders and all the Plantings and Tasks needed for those Orders. So you may see that once you
            create a Recurring Order, depending on how long the lead time is, and when the Recurring Order is supposed
            to start and end, that Microgreen Manager may create several Orders for you to start. After that, Microgreen
            Manager will check multiple times a day, every day, to see if it needs to create new Orders for any of your
            Recurring Orders. It will check the lead time of each of your Recurring Orders, and if the lead time is
            within one week of the current time, it will create a new Order and all the Plantings and Tasks needed for
            that Order.
          </Typography>
          <br />
          <Typography>
            When you check "Create Overdue Orders" when creating a Recurring Order, Microgreen Manager will create any
            Orders that are it deems are too late to plant given the grow time of the Order, and what the fulfillment
            date of the new Order would be. When this isn't checked, Microgreen Manager will not create any Orders that
            are overdue. But when it is checked, Microgreen Manager will create new Orders even if they can't be
            planted/started in time. It will also automatically mark those Orders as "in progress", since it assumes
            that you've already started planting for those Orders. This is primarily useful when you are first getting
            setup with Microgreen Manager and you want Microgreen Manager to accurately reflect the state of your farm.
          </Typography>
          <br />
          <Typography>
            Note that you will then potentially see "overdue" Orders and "overdue" Tasks in your dashboard. You should
            go through the overdue Tasks you see and mark them as complete. Then you should have an accurate view of
            your farm.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Pause a Recurring Order',
      id: 'what-happens-when-i-pause-a-recurring-order',
      aria: 'what-happens-when-i-pause-a-recurring-order-content',
      details: (
        <>
          <Typography>
            Pausing a Recurring Order will temporarily stop Microgreen Manager from creating new Orders for that
            Recurring Order. It will also delete any existing Orders created by that Recurring Order that haven't been
            started yet (where status is "planned"), including any of the Plantings and Tasks associated with those
            Orders. You can always go back and unpause the Recurring Order and Microgreen Manager will start creating
            new Orders for that Recurring Order again.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Edit a Recurring Order',
      id: 'what-happens-when-i-edit-a-recurring-order',
      aria: 'what-happens-when-i-edit-a-recurring-order-content',
      details: (
        <>
          <Typography>
            It depends on which fields you change. If you just edit the Recurring Order name or Recurring Order notes,
            then Microgreen Manager will update the Recurring Order, and will update the name and notes for all the
            Orders that haven't been started yet (where status is "planned").
          </Typography>
          <br />
          <Typography>
            If you edit the start or end times for the Recurring Order, then Microgreen Manager will update the
            Recurring Order, and will delete any existing Orders created by that Recurring Order that haven't been
            started yet (where status is "planned"), and that now fall outside the new start and end times. And then
            will also check to see if any new Orders need to be created that now fall within the new start and end times
            and is coming up in the next week.
          </Typography>
          <br />
          <Typography>Note that you cannot change the start time if the existing start time is in the past.</Typography>
          <br />
          <Typography>
            If you edit the frequency for the Recurring Order, or edit the crops or blends for the Recurring Order, then
            Microgreen Manager will update the Recurring Order, and then delete any existing Orders created by that
            Recurring Order that haven't been started yet (where status is "planned"), and then will create new Orders
            based on the new frequency, new crops and blends, new start and end times, and new customer name and notes
            options.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Delete a Recurring Order',
      id: 'what-happens-when-i-delete-a-recurring-order',
      aria: 'what-happens-when-i-delete-a-recurring-order-content',
      details: (
        <>
          <Typography>
            When you delete a Recurring Order, Microgreen Manager will permanently delete that Recurring Order from the
            database. It will also delete any existing Orders created by that Recurring Order that haven't been started
            yet (where status is "planned"), including any of the Plantings and Tasks associated with those planned
            Orders.
          </Typography>
        </>
      ),
    },
  ],
};
// Plantings
const plantings: Section = {
  id: 'plantings',
  title: 'Plantings',
  content: [
    {
      summary: 'What is a Planting?',
      id: 'what-is-a-planting',
      aria: 'what-is-a-planting-content',
      details: (
        <>
          <Typography>
            A specific crop planted at a specific time for one or more orders. Plantings are for specific crops, even if
            they're involved in a blend. For example, there will never be a Planting for “Nutrition Mix.” Instead, there
            will be three Plantings, one for each broccoli, cabbage, and kohlrabi. One Planting can involve more than
            one tray of a specific crop. One Planting can be a part of more than one Order. For example, the software
            could prompt the user to plant five trays of radish, which will supply three distinct Orders.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Mark a Planting as Complete',
      id: 'what-happens-when-i-mark-a-planting-as-complete',
      aria: 'what-happens-when-i-mark-a-planting-as-complete-content',
      details: (
        <>
          <Typography>
            When you mark a Planting as complete, the software will mark the Planting as complete and will automatically
            mark all the Tasks that belong to that Planting as complete as well.
          </Typography>
          <br />
          <Typography>
            If the Planting belongs to an Order, then the Order will be marked as "in progress" if it isn't already
            marked as "in progress" or "completed".
          </Typography>
          <br />
          <Typography>
            It will also check if the Planting belongs to an Order. If so, it will check if all the Tasks for that Order
            are now marked as complete. If so, then it will automatically mark the Order as complete as well.
          </Typography>
        </>
      ),
    },
  ],
};
// Tasks
const tasks: Section = {
  id: 'tasks',
  title: 'Tasks',
  content: [
    {
      summary: 'What is a Task?',
      id: 'what-is-a-task',
      aria: 'what-is-a-task-content',
      details: (
        <>
          <Typography>
            Individual actions. Tasks can be automatically generated by the software or manually created by the user.
            The software will generate the tasks needed to grow the right crops, at the right time, and in the right
            quantities. These tasks stem from the life stages entered under each Crop. For example, the user creates a
            Crop with steps for soaking, planting, weighted germination, blackout, lights, and harvest. The software
            will use these steps to create corresponding tasks. Users can also manually create one-off and recurring
            tasks for other activities, such as tray cleaning and inventory.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Mark a Task as Complete',
      id: 'what-happens-when-i-mark-a-task-as-complete',
      aria: 'what-happens-when-i-mark-a-task-as-complete-content',
      details: (
        <>
          <Typography>
            When you mark a Task as complete it will update the Task status, but will also automatically update the
            Planting and Order that the Task belongs to. If the Planting and/or Order that the Task belongs to hasn't
            been started yet (status is "planned") then that Planting and/or Order will have their status updated to "in
            progress".
          </Typography>
          <br />
          <Typography>
            Also, Microgreen Manager will automatically check if the Planting that the Task belongs to has all of its
            Tasks marked as complete or cancelled. If so, then Microgreen Manager will automatically mark that Planting
            as complete. And will do the same for the Order that the Task belongs to. If all the Tasks for that Order
            have been marked as complete or cancelled, then Microgreen Manager will automatically mark that Order as
            complete.
          </Typography>
        </>
      ),
    },
  ],
};
// Recurring Tasks
const recurringTasks: Section = {
  id: 'recurring-tasks',
  title: 'Recurring Tasks',
  content: [
    {
      summary: 'Pause a Recurring Task',
      id: 'what-happens-when-i-pause-a-recurring-task',
      aria: 'what-happens-when-i-pause-a-recurring-task-content',
      details: (
        <>
          <Typography>
            Pausing a Recurring Task will temporarily stop Microgreen Manager from creating new Tasks for that Recurring
            Task. It will also delete any existing Tasks created by that Recurring Task that haven't been started yet
            (where status is "planned"). You can always go back and unpause the Recurring Task and Microgreen Manager
            will start creating new Tasks for that Recurring Task again.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Edit a Recurring Task',
      id: 'what-happens-when-i-edit-a-recurring-task',
      aria: 'what-happens-when-i-edit-a-recurring-task-content',
      details: (
        <>
          <Typography>
            If you just edit the task name or task notes, then Microgreen Manager will update the Recurring Task, and
            will update the name and notes for all the Tasks that haven't been started yet (where status is "planned")
            that were created by that Recurring Task. Any new Tasks created going forward of course will have the new
            values as well.
          </Typography>
          <br />
          <Typography>
            If you edit the start or end times for the Recurring Task, then Microgreen Manager will update the Recurring
            Task, and will delete any existing Tasks created by that Recurring Task that haven't been started yet (where
            status is "planned") and that now fall outside the new start and end times. And then will also check to see
            if any new Tasks need to be created that now fall within the new start and end times and is coming up in the
            next week.
          </Typography>
          <br />
          <Typography>
            If you edit the frequency for the Recurring Task, then Microgreen Manager will update the Recurring Task,
            and then delete any existing Tasks created by that Recurring Task that haven't been started yet (where
            status is "planned"), and then will create new Tasks based on the new frequency.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Delete a Recurring Task',
      id: 'what-happens-when-i-delete-a-recurring-task',
      aria: 'what-happens-when-i-delete-a-recurring-task-content',
      details: (
        <>
          <Typography>
            When you delete a Recurring Task, Microgreen Manager will permanently delete that Recurring Task from the
            database. It will also delete any existing Tasks created by that Recurring Task that haven't been started
            yet (where status is "planned"). Previous Tasks from that Recurring Task that have been started or completed
            will not be deleted.
          </Typography>
        </>
      ),
    },
  ],
};
// Harvests
const harvests: Section = {
  id: 'harvests',
  title: 'Harvests',
  content: [
    {
      summary: 'What is a Harvest?',
      id: 'what-is-a-harvest',
      aria: 'what-is-a-harvest-content',
      details: (
        <>
          <Typography>
            Harvesting is the process of cutting and packing mature microgreens. A specific Harvest, though, is the
            collection of all harvesting taking place on a specific day. For example, the user could have 180 trays to
            harvest across 12 Crops for 20 customers
          </Typography>
        </>
      ),
    },
  ],
};
// Team
const team: Section = {
  id: 'team',
  title: 'Team',
  content: [
    {
      summary: 'Invite Team Members To Your Farm',
      id: 'how-do-i-invite-someone-to-join-my-farm',
      aria: 'how-do-i-invite-someone-to-join-my-farm-content',
      details: (
        <>
          <Typography>
            If you are an "admin" for your farm, you can invite other people to join your farm. You can do this by going
            to the account page and clicking the Options button. You'll see an option to "Invite User to Farm". Click
            that and you'll see a form popup, where you can enter their name and email address. They will receive an
            email at the address provided with a link to create an account. The email you provide does not have to be
            the same email they use to sign up. This email is just where the invite will be sent. Once they create their
            account they will be able to see your farm and all of its data.
          </Typography>
          <br />
          <Typography>
            By default, when they first join your farm they won't be able to create or edit any data. Once they join
            your farm you can go back to the Account page. On your Account page, as an admin, you'll see any pending
            invites you've sent out, and you'll see any current farm members. If the data looks out of date, you can
            refresh the data by going to your Options dropdown and clicking "Refresh Account Info". Now you should see a
            "Farm Members" table with your current farm members. Click the Permissions dropdown on the far right to see
            and edit the permissions for that farm member. You can always cancel pending invites or remove members from
            your farm.
          </Typography>
        </>
      ),
    },
    {
      summary: 'Team Member Permissions',
      id: 'what-are-the-different-permissions-for-farm-members',
      aria: 'what-are-the-different-permissions-for-farm-members-content',
      details: (
        <>
          <Typography>
            Right now, the only "admin" in any farm is the person who first created the farm/account in Microgreen
            Manager. Admins will be able to invite other people to join their farm, and will be able to edit the
            permissions of other farm members. They can also see and edit all the data for the farm, including farm
            members and farm invites.
          </Typography>
          <br />
          <Typography>
            Users invited to the farm by admins will by default be able to see all the data for the farm, but will not
            be able to edit any data. This means they won't be able to create orders, plantings, tasks, or harvests.
            They also won't be able to edit any of the data for the farm, including not being able to mark tasks as
            complete. But you can give them some permissions to edit data if you want:
          </Typography>
          <br />
          <ul>
            <li>
              <b>Can Modify Orders</b> - This permission will allow the user to create, edit, and delete orders or
              recurring orders for the farm.
            </li>
            <li>
              <b>Can Modify Crops and Blends</b> - This permission will allow the user to create, edit, and delete crops
              and blends for the farm.
            </li>
            <li>
              <b>Can Modify Tasks</b> - This permission will allow the user to create, edit, and delete tasks for the
              farm.
            </li>
            <li>
              <b>Can Complete Tasks</b> - This permission will allow the user to mark tasks as complete for the farm,
              and by extension mark plantings and orders as complete.
            </li>
          </ul>
        </>
      ),
    },
    {
      summary: 'Remove a Team Member From Your Farm',
      id: 'how-do-i-remove-someone-from-my-farm',
      aria: 'how-do-i-remove-someone-from-my-farm-content',
      details: (
        <>
          <Typography>
            Click the remove icon on a farm members in the Farm Members table on your account page to remove a farm
            member. This will remove them from your farm, and they will no longer be able to see your farm or any of its
            data. Their account will be deleted from Microgreen Manager. They can create another account with their
            email, but they will no longer have access to your farm or any of its data, unless you invite them again.
          </Typography>
          <br />
          <Typography>
            If a farm member has marked any tasks as completed in your farm, Microgreen Manager will record who
            completed that task or tasks. If that person then deletes their account, Microgreen Manager will still
            retain the name of who completed those tasks, for historical record keeping.
          </Typography>
        </>
      ),
    },
  ],
};
// Account
const account: Section = {
  id: 'account',
  title: 'Account',
  content: [
    {
      summary: 'How to Delete Your Account',
      id: 'how-do-i-delete-my-account',
      aria: 'how-do-i-delete-my-account-content',
      details: (
        <>
          <Typography>
            You can delete your account by going to the Account page and clicking the Delete Account button. It will ask
            you if you are sure you want to delete your account, and to be extra safe it will request that you type in
            your full name to confirm that you want to delete your account. Once you confirm and press submit your
            account will be deleted, all your data will be deleted, and you will be logged out. This action is
            irreversible, but you can always create a new account if you want to start using Microgreen Manager again.
          </Typography>
          <br />
          <Typography>
            If you are the only admin for your farm, then deleting your account will also delete your farm and all its
            data, including all the accounts of all the other farm members. If you are not the only admin for your farm,
            then deleting your account will not delete your farm, it will only delete your account and nothing else.
          </Typography>
          <br />
          <Typography>
            Farm members can delete their own accounts, but they cannot delete the farm or any other accounts within
            your farm. No other data in your farm will be affected if one of your farm members deletes their own
            account.
          </Typography>
        </>
      ),
    },
  ],
};

export default function NewFAQView() {
  return (
    <Box>
      <Typography variant="body1">
        We strive to make Microgreen Manager easy to use and easy to understand, but we've also created concise video
        tutorials to help you get started. Select one of our videos below to get started. Or if you prefer to read about
        Microgreen Manager, we have a comprehensive FAQ below.
      </Typography>
      <Box sx={{ pt: 2 }}>
        <VideoGrid />
      </Box>
      {[
        mm,
        priceCalculator,
        crops,
        blends,
        orders,
        recurringOrders,
        plantings,
        harvests,
        tasks,
        recurringTasks,
        team,
        account,
      ].map(createSection)}
    </Box>
  );
}
